import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core'
import { FlexModule } from '@angular/flex-layout'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatChipListboxChange, MatChipsModule } from '@angular/material/chips'
import { MatDialog, MatDialogModule } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar'
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort'
import { MatTableModule } from '@angular/material/table'
import { MatTooltipModule } from '@angular/material/tooltip'
import { AccountClearanceStatusToColorPipe, PipesModule } from '@mmx/pipes'
import {
  AppointmentDataService,
  CONFIGURATION_KEYS,
  ConfigurationService,
  CrudService,
  DirectivesModule,
  FacilityService,
  PATIENT_ACCOUNT_PROCEDURE_CLEARANCE_STATUS,
  patientAccountProcedureClearanceStatusPriority,
  PatientAccountClearanceModel,
  PatientAccountClearanceService,
  PatientInsurance,
} from '@mmx/shared'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { orderBy } from 'lodash'
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, finalize, map, skip, Subject, switchMap, take, takeUntil, tap } from 'rxjs'

import { AuthorizationDialogComponent, AuthorizationDialogModule } from '../../../authorization-dialog'

interface SortCriteria {
  active: string
  direction: SortDirection
}

const DEFAULT_SORT: SortCriteria = { active: 'coverage', direction: 'asc' }
const COURSE_OF_CARE_SORT: SortCriteria = { active: 'clearanceStatus', direction: 'asc' }

@Component({
  selector: 'mmx-patient-account-clearance-card',
  templateUrl: './patient-account-clearance-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FlexModule,
    MatIconModule,
    MatTooltipModule,
    MatTableModule,
    MatSortModule,
    MatCardModule,
    TranslateModule,
    MatButtonModule,
    MatChipsModule,
    MatSlideToggleModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressBarModule,
    AccountClearanceStatusToColorPipe,
    AuthorizationDialogModule,
    DirectivesModule,
    PipesModule,
  ],
  providers: [
    ConfigurationService,
  ],
})
export class PatientAccountClearanceCardComponent implements OnInit, OnDestroy {
  private selectedInsurance$ = new BehaviorSubject<PatientInsurance | null>(null)
  public showAuthNotScheduledCpts$ = new BehaviorSubject<boolean>(false)
  public courseOfCareToggleEnabled$ = new BehaviorSubject<boolean>(false)
  public showAllCourseOfCareCpts$ = new BehaviorSubject<boolean>(false)

  public readonly displayedColumns$ = this.courseOfCareToggleEnabled$.pipe(
    map((courseOfCareToggleEnabled) => [
      'code',
      ...(courseOfCareToggleEnabled ? ['appointmentDate'] : []),
      'coverage',
      'authorizationRequired',
      'authorizationNumber',
      'clearanceStatus',
    ]),
  )

  public loading$ = new BehaviorSubject(true)

  public isAppointmentCanceled$ = this.appointmentDataService.appointment$
    .pipe(
      map((appointment) => appointment.isCanceled),
    )

  public isReprocessDisabled$ = combineLatest([
    this.isAppointmentCanceled$,
    this.loading$,
  ]).pipe(
    map(([isCanceled, loading]) => isCanceled || loading),
  )

  public sortCriteria$ = new BehaviorSubject<SortCriteria>(DEFAULT_SORT)

  public account$ = this.appointmentDataService.appointment$
    .pipe(
      map(({ account }) => account),
    )

  public accountClearances$ = this.appointmentDataService.appointment$
    .pipe(
      filter((appointment) => !!appointment.account),
      tap(() => this.loading$.next(true)),
      switchMap(({ accountId }) => this.crudService.index(PatientAccountClearanceModel, {
        accountId,
      }).pipe(
        finalize(() => this.loading$.next(false)),
      )),
      map(clearances => orderBy(clearances, clearance => [clearance.patientInsurance?.typeIndex ?? 0, clearance.code])),
    )

  public appointmentCptCodes$ = this.appointmentDataService.appointment$.pipe(
    map((appointment) => appointment.products?.flatMap((p) => p.getCPTCodes()) ?? []),
  )

  public filteredAccountClearances$ = combineLatest([
    this.accountClearances$,
    this.selectedInsurance$,
    this.sortCriteria$,
    this.appointmentDataService.appointment$,
    this.appointmentCptCodes$,
    this.showAuthNotScheduledCpts$,
    this.showAllCourseOfCareCpts$,
  ]).pipe(
    filter(([clearances]) => !!clearances?.length),
    map(([clearances, selectedInsurance, sort, appointment, appointmentCptCodes, showAuthNotScheduledCpts, showAllCourseOfCareCpts]) => {
      let filteredClearances = (!selectedInsurance
        ? clearances
        : clearances.filter((clearance) => clearance.insuranceId === selectedInsurance.id))

      filteredClearances = filteredClearances.filter((clearance) => {
        const isScheduledOnCurrentAppointment = this.isScheduledOnCurrentAppointment(clearance, appointment.id, appointmentCptCodes)
        const isAuthorizationOnlyClearance = this.isAuthorizationOnlyClearance(clearance, appointment.id, appointmentCptCodes)

        if (showAllCourseOfCareCpts) {
          return true
        }

        if (showAuthNotScheduledCpts) {
          return isScheduledOnCurrentAppointment || isAuthorizationOnlyClearance
        }

        return isScheduledOnCurrentAppointment
      })

      if (!sort?.active || !sort?.direction) {
        return filteredClearances
      }

      const sortValue = (clearance: PatientAccountClearanceModel) => {
        switch (sort.active) {
          case 'code':
            return clearance.code
          case 'appointmentDate':
            return this.appointmentSortValue(clearance)
          case 'coverage':
            return clearance.patientInsurance?.clinicCarrierName ?? ''
          case 'authorizationRequired':
            return clearance.isAuthRequired ? 1 : 0
          case 'authorizationNumber':
            return clearance.authorization?.number ?? ''
          case 'clearanceStatus':
            return this.clearanceStatusSortValue(clearance)
        }
      }

      const isUndated = (clearance: PatientAccountClearanceModel) => this.appointmentSortValue(clearance) == null

      if (sort.active === 'appointmentDate') {
        return orderBy(filteredClearances, [isUndated, sortValue], ['asc', sort.direction])
      }

      if (sort.active === 'clearanceStatus') {
        const isUnrecognizedStatus = (clearance: PatientAccountClearanceModel) =>
          !patientAccountProcedureClearanceStatusPriority.includes(clearance.status)

        return orderBy(
          filteredClearances,
          [isUnrecognizedStatus, sortValue, (clearance) => this.appointmentSortValue(clearance)],
          ['asc', sort.direction, 'asc'],
        )
      }

      return orderBy(filteredClearances, [sortValue], [sort.direction])
    }),
  )

  public tableData$ = combineLatest([
    this.filteredAccountClearances$,
    this.appointmentDataService.appointment$,
    this.appointmentCptCodes$,
    this.displayedColumns$,
    this.sortCriteria$,
  ]).pipe(
    map(([clearances, appointment, appointmentCptCodes, displayedColumns, sort]) => ({
      clearances,
      appointment,
      appointmentCptCodes,
      displayedColumns,
      sort,
    })),
  )

  public clearanceStatuses = PATIENT_ACCOUNT_PROCEDURE_CLEARANCE_STATUS
  private onDestroy$ = new Subject<void>()

  constructor(
    public readonly appointmentDataService: AppointmentDataService,
    private configurationService: ConfigurationService,
    private crudService: CrudService,
    private facilityService: FacilityService,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private translateService: TranslateService,
    private patientAccountClearanceService: PatientAccountClearanceService,
  ) { }

  public ngOnInit() {
    this.configurationService.index([
      CONFIGURATION_KEYS.CLEARANCE_SHOW_AUTH_NOT_SCHEDULED_CPTS,
      CONFIGURATION_KEYS.CLEARANCE_SHOW_ALL_COURSE_OF_CARE_CPTS,
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(config => {
        this.showAuthNotScheduledCpts$.next(config[CONFIGURATION_KEYS.CLEARANCE_SHOW_AUTH_NOT_SCHEDULED_CPTS] ?? false)
        this.courseOfCareToggleEnabled$.next(config[CONFIGURATION_KEYS.CLEARANCE_SHOW_ALL_COURSE_OF_CARE_CPTS] ?? false)
      })

    this.appointmentDataService.appointment$.pipe(
      takeUntil(this.onDestroy$),
      map(({ id }) => id),
      distinctUntilChanged(),
      skip(1),
    ).subscribe(() => {
      this.showAllCourseOfCareCpts$.next(false)
      this.sortCriteria$.next(DEFAULT_SORT)
    })
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public onCourseOfCareToggle({ checked }: MatSlideToggleChange): void {
    this.showAllCourseOfCareCpts$.next(checked)
    this.sortCriteria$.next(checked ? COURSE_OF_CARE_SORT : DEFAULT_SORT)
  }

  public onInsuranceSelectionChange({ value }: MatChipListboxChange) {
    const patientInsurance = value as PatientInsurance
    this.selectedInsurance$.next(patientInsurance)
  }

  public isCurrentAppointmentClearance(clearance: PatientAccountClearanceModel, appointmentId: string): boolean {
    return !!clearance.appointmentId && clearance.appointmentId === appointmentId
  }

  /** Returns the sortable instant, or `undefined` when the row has no usable appointment date. */
  public appointmentSortValue(clearance: PatientAccountClearanceModel): number | undefined {
    return clearance.appointmentStartAt?.isValid() ? clearance.appointmentStartAt.valueOf() : undefined
  }

  /** Ranks a row by outstanding authorization work, with unrecognized statuses ranked last. */
  private clearanceStatusSortValue(clearance: PatientAccountClearanceModel): number {
    const rank = patientAccountProcedureClearanceStatusPriority.indexOf(clearance.status)

    return rank === -1 ? patientAccountProcedureClearanceStatusPriority.length : rank
  }

  public isScheduledOnCurrentAppointment(
    clearance: PatientAccountClearanceModel,
    appointmentId: string,
    appointmentCptCodes: string[],
  ): boolean {
    return this.isCurrentAppointmentClearance(clearance, appointmentId) && appointmentCptCodes.includes(clearance.code)
  }

  public isAuthorizationOnlyClearance(
    clearance: PatientAccountClearanceModel,
    appointmentId: string,
    appointmentCptCodes: string[],
  ): boolean {
    return !!clearance.authorizationId
      && !appointmentCptCodes.includes(clearance.code)
      && (!clearance.appointmentId || clearance.appointmentId === appointmentId)
  }

  public getClearanceSourceLabelKey(clearance: PatientAccountClearanceModel, appointmentId: string, appointmentCptCodes: string[]): string {
    if (this.isScheduledOnCurrentAppointment(clearance, appointmentId, appointmentCptCodes)) {
      return 'patient-account-clearance.current-appointment'
    }

    if (this.isAuthorizationOnlyClearance(clearance, appointmentId, appointmentCptCodes)) {
      return 'patient-account-clearance.auth-only'
    }

    return 'patient-account-clearance.course-of-care'
  }

  public getClearanceSourceTooltipKey(clearance: PatientAccountClearanceModel, appointmentId: string, appointmentCptCodes: string[]): string {
    if (this.isScheduledOnCurrentAppointment(clearance, appointmentId, appointmentCptCodes)) {
      return 'patient-account-clearance.current-appointment-tooltip'
    }

    if (this.isAuthorizationOnlyClearance(clearance, appointmentId, appointmentCptCodes)) {
      return 'patient-account-clearance.auth-only-tooltip'
    }

    return 'patient-account-clearance.course-of-care-tooltip'
  }

  public shouldShowClearanceSourceBadge(
    clearance: PatientAccountClearanceModel,
    appointmentId: string,
    appointmentCptCodes: string[],
    showAuthNotScheduledCpts: boolean,
    showAllCourseOfCareCpts: boolean,
  ): boolean {
    if (showAllCourseOfCareCpts) {
      return true
    }

    return showAuthNotScheduledCpts && this.isAuthorizationOnlyClearance(clearance, appointmentId, appointmentCptCodes)
  }

  public addAuthorization(patientInsurance: PatientInsurance) {
    combineLatest([
      this.appointmentDataService.appointment$,
      this.appointmentDataService.patient$,
    ])
      .pipe(
        take(1),
        switchMap(([appointment, patient]) => {
          return this.dialog.open(AuthorizationDialogComponent, {
            width: '800px',
            data: {
              appointment,
              patient,
              patientInsurances: [patientInsurance],
              tz: this.facilityService.currentTimezone(),
            },
            panelClass: 'dialog-form',
          }).afterClosed()
        }),
        filter(Boolean),
      )
      .subscribe({
        next: () => {
          this.openSnackBar(
            this.translateService.instant('patient-account-clearance.add-authorization-success'),
          )
          this.appointmentDataService.appointmentUpdated$.next()
        },
        error: () => this.openSnackBar(
          this.translateService.instant('patient-account-clearance.add-authorization-error'),
        ),
      })
  }

  public sortData(e: Sort) {
    this.sortCriteria$.next({ active: e.active, direction: e.direction })
  }

  public reprocessAccount(account: PatientAccountClearanceModel) {
    this.loading$.next(true)
    this.patientAccountClearanceService.rerunPatientAccountClearance(account.id)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.loading$.next(false)),
      )
      .subscribe({
        next: () => {
          this.openSnackBar(
            this.translateService.instant('patient-account-clearance.reprocess-account-success'),
          )
          this.appointmentDataService.appointmentUpdated$.next()
        },
        error: () => {
          this.openSnackBar(
            this.translateService.instant('patient-account-clearance.reprocess-account-error'),
          )
        },
      })
  }

  private openSnackBar(message: string) {
    this.snackbar.open(message, null, { duration: 5000 })
  }
}
